import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import {
  addEquipment,
  addInstallation,
  addressBody,
  addressErrors,
  addressLine,
  dateLabel,
  deviceTypeLabel,
  equipmentBody,
  equipmentErrors,
  getInstallations,
  isRemoved,
  newEquipment,
  removeEquipment,
  EMPTY_ADDRESS,
  type AddressForm,
  type EquipmentForm,
  type EquipmentInput,
  type InstallationInput,
  type Installations,
  type InstalledEquipment,
  type LoginInstallation,
} from "../../api/installations";
import { AddressFields, EquipmentRow } from "./InstallationFields";

/**
 * A subscriber's installations (owner §10, 2026-10-14): per login, where it is
 * installed and the equipment in place or removed -- for recovery. Add address
 * (when a login has none), Add equipment, Remove (sets a removal date; nothing
 * is deleted).
 */
export function SubscriberInstallationsDialog({ ns, subscriber, onClose }: { ns: string; subscriber: InvoicePrefSubscriber | null; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const accountId = subscriber?.accountid ?? "";
  const key = ["installations", ns, accountId];
  const list = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns && accountId),
    queryFn: ({ signal }) => getInstallations(identity, ns, accountId, signal),
    retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const addAddr = useMutation({ mutationFn: (b: InstallationInput) => addInstallation(identity, ns, accountId, b), onSuccess: refresh });
  const addEq = useMutation({ mutationFn: (v: { locationId: string; body: EquipmentInput }) => addEquipment(identity, ns, accountId, v.locationId, v.body), onSuccess: refresh });
  // A 409 (removed already) or 404 means the list is stale: reload it either way.
  const remove = useMutation({ mutationFn: (v: { locationId: string; equipmentId: string }) => removeEquipment(identity, ns, accountId, v.locationId, v.equipmentId), onSettled: refresh });
  const close = () => {
    addAddr.reset();
    addEq.reset();
    remove.reset();
    onClose();
  };
  const err = list.error ?? addAddr.error ?? addEq.error ?? remove.error;

  return (
    <Dialog open={subscriber !== null} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Installations</DialogTitle>
      {subscriber && (
        <DialogContent>
          <InstallationsPanel
            who={subscriber.name || subscriber.username || subscriber.accountid}
            data={list.data ?? null}
            loading={list.isLoading}
            error={err ? (err instanceof Error ? err.message : String(err)) : null}
            pending={addAddr.isPending || addEq.isPending || remove.isPending}
            onAddAddress={(b) => addAddr.mutate(b)}
            onAddEquipment={(locationId, body) => addEq.mutate({ locationId, body })}
            onRemove={(locationId, equipmentId) => remove.mutate({ locationId, equipmentId })}
          />
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export interface InstallationsPanelProps {
  who: string;
  /** null while loading or when the load failed. */
  data: Installations | null;
  loading?: boolean;
  error?: string | null;
  pending?: boolean;
  onAddAddress: (b: InstallationInput) => void;
  onAddEquipment: (locationId: string, body: EquipmentInput) => void;
  onRemove: (locationId: string, equipmentId: string) => void;
}

/** The view without its data hooks, so a test can render it. */
export function InstallationsPanel(p: InstallationsPanelProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography variant="body2">{p.who}</Typography>
      {p.data?.billing_address && (
        <Typography variant="caption" color="text.secondary">
          Billing address: {addressLine(p.data.billing_address)}
        </Typography>
      )}
      {p.error && <Alert severity="error">{p.error}</Alert>}
      {p.loading && <Typography variant="body2">Loading…</Typography>}
      {p.data && p.data.logins.length === 0 && <Alert severity="info">No logins with installations. A hotspot subscriber has none.</Alert>}
      {p.data?.logins.map((l) => (
        <LoginBlock key={l.username} login={l} pending={Boolean(p.pending)} onAddAddress={p.onAddAddress} onAddEquipment={p.onAddEquipment} onRemove={p.onRemove} />
      ))}
    </Box>
  );
}

function LoginBlock({
  login,
  pending,
  onAddAddress,
  onAddEquipment,
  onRemove,
}: {
  login: LoginInstallation;
  pending: boolean;
  onAddAddress: InstallationsPanelProps["onAddAddress"];
  onAddEquipment: InstallationsPanelProps["onAddEquipment"];
  onRemove: InstallationsPanelProps["onRemove"];
}) {
  const [addingAddress, setAddingAddress] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [addr, setAddr] = useState<AddressForm>(EMPTY_ADDRESS);
  const [addingEq, setAddingEq] = useState<EquipmentForm | null>(null);
  const [confirm, setConfirm] = useState<InstalledEquipment | null>(null);
  const [touched, setTouched] = useState(false);
  const loc = login.location;
  // The device login's id pre-fills an ONU's MAC; a mobile login (10 digits) is not a MAC.
  const isDeviceLogin = !/^\d{10}$/.test(login.username);
  const role = isDeviceLogin ? "device" : "mobile";
  const addrProblems = sameAsBilling ? [] : addressErrors(addr, "Address");
  const eqProblems = addingEq ? equipmentErrors(addingEq, "Equipment") : [];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="subtitle2">
        Login <code>{login.username}</code>
      </Typography>
      {loc ? (
        <Typography variant="body2">
          {addressLine(loc) || loc.label || "—"}
          {loc.created_at && (
            <Typography component="span" variant="caption" color="text.secondary">
              {" "}
              · since {dateLabel(loc.created_at)}
            </Typography>
          )}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            No installation address.
          </Typography>
          {!addingAddress ? (
            <Box>
              <Button size="small" onClick={() => setAddingAddress(true)} disabled={pending}>
                Add address
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormControlLabel control={<Checkbox size="small" checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} />} label="Installed at the billing address" />
              {!sameAsBilling && <AddressFields value={addr} onChange={setAddr} disabled={pending} />}
              {touched && addrProblems.length > 0 && <Alert severity="warning">{addrProblems.join("; ")}</Alert>}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={() => setAddingAddress(false)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={pending}
                  onClick={() => {
                    setTouched(true);
                    if (addrProblems.length) return;
                    onAddAddress(sameAsBilling ? { username: login.username, same_as_billing: true } : { username: login.username, same_as_billing: false, ...addressBody(addr) });
                    setAddingAddress(false);
                    setTouched(false);
                  }}
                >
                  Save address
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {login.equipment.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Make / model</TableCell>
              <TableCell>Serial</TableCell>
              <TableCell>MAC</TableCell>
              <TableCell>Installed</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {login.equipment.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{deviceTypeLabel(e.device_type)}</TableCell>
                <TableCell>{[e.make, e.model].filter(Boolean).join(" ") || "—"}</TableCell>
                <TableCell>{e.serial || "—"}</TableCell>
                <TableCell>
                  <code>{e.mac_address || "—"}</code>
                </TableCell>
                <TableCell>{dateLabel(e.installed_at)}</TableCell>
                <TableCell>{isRemoved(e) ? <Chip size="small" label={`Removed ${dateLabel(e.removed_at)}`} /> : <Chip size="small" color="success" label="In place" />}</TableCell>
                <TableCell align="right">
                  {!isRemoved(e) && loc && (
                    <Button size="small" color="error" disabled={pending} onClick={() => setConfirm(e)}>
                      Remove
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {confirm && loc && (
        <Alert
          severity="warning"
          action={
            <>
              <Button size="small" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                color="error"
                disabled={pending}
                onClick={() => {
                  onRemove(loc.id, confirm.id);
                  setConfirm(null);
                }}
              >
                Confirm remove
              </Button>
            </>
          }
        >
          Mark {deviceTypeLabel(confirm.device_type)} {confirm.serial || confirm.mac_address} as removed today? The record is kept.
        </Alert>
      )}

      {loc &&
        (addingEq ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <EquipmentRow value={addingEq} onChange={setAddingEq} role={role} deviceid={isDeviceLogin ? login.username : ""} disabled={pending} />
            {touched && eqProblems.length > 0 && <Alert severity="warning">{eqProblems.join("; ")}</Alert>}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => setAddingEq(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={pending}
                onClick={() => {
                  setTouched(true);
                  if (eqProblems.length) return;
                  onAddEquipment(loc.id, equipmentBody(addingEq));
                  setAddingEq(null);
                  setTouched(false);
                }}
              >
                Save equipment
              </Button>
            </Box>
          </Box>
        ) : login.equipment.some((e) => !isRemoved(e)) ? (
          // One device per installation (owner 2026-10-19): remove the one in place to replace it.
          <Typography variant="caption" color="text.secondary">
            One device per installation — remove the one in place to replace it.
          </Typography>
        ) : (
          <Box>
            <Button size="small" disabled={pending} onClick={() => setAddingEq(newEquipment(role, isDeviceLogin ? login.username : ""))}>
              Add equipment
            </Button>
          </Box>
        ))}
    </Paper>
  );
}
